import { CompanyService } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';

export interface CompanyServiceResponse {
    id: number
    uuid: string
    created_at: string
    updated_at: string
    archived_at: string | null
    name: string
    type: string
    unit: string
    when_to_charge: 'attached' | 'always'
    // The three price keys are omitted entirely — not nulled — for a caller without the
    // company_services:view_price privilege (a merchant assistant, say). The same privilege gates
    // every price the UI renders, so an omitted charge is never displayed; it is carried as 0 so the
    // rest of the service (its name, its type) still resolves.
    charge?: number | string
    item_age_filter: number | string | null
    percentage_charge?: number | string | null
    service_provider_id: number
    is_available_for_merchants: boolean
    price_records?: Array<{
        amount: number | string
        effective_before: string
        set_at: string
        set_by_user_id: number
    }>
}

const numberOrNull = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    return Number(value);
};

export function toCompanyService(cs: CompanyServiceResponse): CompanyService {
    return {
        id: cs.id,
        uuid: cs.uuid,
        createdAt: cs.created_at,
        updatedAt: cs.updated_at,
        archivedAt: cs.archived_at,
        name: cs.name,
        type: cs.type,
        unit: cs.unit,
        whenToCharge: cs.when_to_charge,
        charge: cs.charge === undefined ? 0 : Number(cs.charge),
        itemAgeFilter: numberOrNull(cs.item_age_filter),
        percentageCharge: numberOrNull(cs.percentage_charge),
        serviceProviderId: cs.service_provider_id,
        isAvailableForMerchants: cs.is_available_for_merchants,
        priceRecords: cs.price_records?.map(priceRecord => ({
            amount: Number(priceRecord.amount),
            effectiveBefore: priceRecord.effective_before,
            setAt: priceRecord.set_at,
            setByUserId: priceRecord.set_by_user_id,
        })),
    };
}

interface CompanyServicesApiReturn {
    fetchCompanyServices: (type?: string[]) => Promise<CompanyService[]>
    fetchCompanyService: (companyServiceId: number) => Promise<CompanyService>
    createCompanyService: (payload: CompanyServiceFormPayload) => Promise<void>
    updateCompanyService: (companyServiceId: number, payload: CompanyServiceFormPayload) => Promise<void>
    deleteService: (companyServiceId: number) => Promise<void>
    archiveService: (companyServiceId: number) => Promise<void>
    fetchArchivedServices: (type?: string[]) => Promise<CompanyService[]>
    fetchOutboundShipmentAttachedServices: (outboundShipmentId: number) => Promise<CompanyService[]>
}

export interface CompanyServiceFormPayload {
    name: string
    type: string
    unit: string
    charge: number
    percentageCharge?: number | string | null
    whenToCharge: string
    itemAgeFilter?: boolean | number | string | null
    isAvailableForMerchants: boolean
}

interface CompanyServiceRequest {
    name: string
    type: string
    unit: string
    charge: number
    percentage_charge?: number | string | null
    when_to_charge: string
    item_age_filter?: boolean | number | string | null
    is_available_for_merchants: boolean
}

const toCompanyServiceRequest = (payload: CompanyServiceFormPayload): CompanyServiceRequest => ({
    name: payload.name,
    type: payload.type,
    unit: payload.unit,
    charge: payload.charge,
    percentage_charge: payload.percentageCharge,
    when_to_charge: payload.whenToCharge,
    item_age_filter: payload.itemAgeFilter,
    is_available_for_merchants: payload.isAvailableForMerchants,
});

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): CompanyServicesApiReturn => ({
    fetchCompanyServices: async (type?: string[]): Promise<CompanyService[]> => {
        let uri = '/api/services';
        if (type && type.length) {
            uri += `?type[]=${type.join('&type[]=')}`;
        }

        const response = await axios.get<{ services: CompanyServiceResponse[] }>(uri);

        return response.data.services.map(toCompanyService);
    },

    fetchCompanyService: async (companyServiceId: number): Promise<CompanyService> => {
        const response = await axios.get<{ service: CompanyServiceResponse }>(`/api/services/${companyServiceId}`);

        return toCompanyService(response.data.service);
    },

    createCompanyService: async (payload: CompanyServiceFormPayload): Promise<void> => {
        await axios.post('/api/services', toCompanyServiceRequest(payload));
    },

    updateCompanyService: async (companyServiceId: number, payload: CompanyServiceFormPayload): Promise<void> => {
        await axios.put(`/api/services/${companyServiceId}`, toCompanyServiceRequest(payload));
    },

    deleteService: async (companyServiceId: number): Promise<void> => {
        await axios.delete(`/api/services/${companyServiceId}`);
    },

    archiveService: async (companyServiceId: number): Promise<void> => {
        await axios.post(`/api/services/${companyServiceId}/archive`);
    },

    fetchArchivedServices: async (type?: string[]): Promise<CompanyService[]> => {
        let uri = '/api/services?archived';
        if (type && type.length) {
            uri += `&type[]=${type.join('&type[]=')}`;
        }

        const response = await axios.get<{ services: CompanyServiceResponse[] }>(uri);

        return response.data.services.map(toCompanyService);
    },

    // Every company service already attached to one outbound shipment — the shipment's own service
    // lines and those on its product lines. Deliberately not a catalog: unlike /api/services it is
    // unfiltered by is_available_for_merchants and includes archived and soft-deleted services, so
    // an attached line always resolves, for a merchant user as much as a service-provider one. It
    // returns nothing that is not already attached, so it must never populate a service picker.
    fetchOutboundShipmentAttachedServices: async (outboundShipmentId: number): Promise<CompanyService[]> => {
        const response = await axios.get<{ data: CompanyServiceResponse[] }>(`/api/shipments/outbound/${outboundShipmentId}/services`);

        return response.data.data.map(toCompanyService);
    },
});
